const { Core, Utils, Events } = Global;
const { $toggleDisplay, $loading } = Utils;

const OBSERVER_BOTTOM_OFFSET = '25%';
const EVENT_SRC = 'dynamic-pagination';
const RETURN_STATE_NAME = 'yfCollectionReturn';
const RETURN_STORAGE_KEY = 'yfCollectionReturn';
const RETURN_STATE_MAX_AGE = 4 * 60 * 60 * 1000;
const RETURN_RESTORING_CLASS = 'yf-collection-return-restoring';

function collectionKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');

  const sortedParams = new URLSearchParams();
  Array.from(url.searchParams.entries())
    .sort(([keyA, valueA], [keyB, valueB]) => {
      if (keyA === keyB) return String(valueA).localeCompare(String(valueB));
      return String(keyA).localeCompare(String(keyB));
    })
    .forEach(([key, value]) => sortedParams.append(key, value));

  const query = sortedParams.toString();
  return `${url.pathname}${query ? `?${query}` : ''}`;
}

function currentUrlKey() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isBackForwardNavigation() {
  const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
  return navigationEntry?.type === 'back_forward' || performance.navigation?.type === 2;
}

function readReturnState() {
  const historyState = history.state && typeof history.state === 'object'
    ? history.state[RETURN_STATE_NAME]
    : null;

  if (historyState) return historyState;

  try {
    return JSON.parse(sessionStorage.getItem(RETURN_STORAGE_KEY) || 'null');
  } catch (error) {
    return null;
  }
}

function clearReturnState() {
  const currentState = history.state && typeof history.state === 'object'
    ? { ...history.state }
    : {};

  delete currentState[RETURN_STATE_NAME];
  history.replaceState(currentState, '', window.location.href);

  try {
    sessionStorage.removeItem(RETURN_STORAGE_KEY);
  } catch (error) {
    // History state remains the primary storage mechanism.
  }
}

function hideCollectionForRestore() {
  document.documentElement.classList.add(RETURN_RESTORING_CLASS);
  window.__yfCollectionReturnRestorePending = true;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
}

customElements.define('collection-dynamic-pagination', class extends Core {
  elements = {
    $: ['load-button', 'input']
  };

  propTypes = {
    'total-pages': Number,
    'current-page': Number,
    'type': String,
    'form-id': String
  };

  render() {
    this.sub(Events.COLLECTION_UPDATED, this._handleCollectionUpdate);

    this._initCurrentState();
    this._handleTrigger();
    this._checkEdge();
    this._defaultLoadingLabel = this.querySelector('h5')?.textContent?.trim() || 'Loading products';

    if (this._isSearchGrid() && !this._yfSearchRetryBound) {
      this._yfSearchRetryBound = true;
      this.addEventListener('click', () => {
        if (this.hasAttribute('data-yf-load-error')) this._loadNextPage();
      });
      this.addEventListener('keydown', (event) => {
        if (
          this.hasAttribute('data-yf-load-error')
          && (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          this._loadNextPage();
        }
      });
    }

    window.setTimeout(() => this._beginReturnRestore(), 0);
  }

  _handleTrigger() {
    switch (this.prop('type')) {
      case 'scroll':
        this._handleEdgeScroll();
        this.$loadingTarget = this;
        break;
      case 'button':
        this._handleButton();
        this.$loadingTarget = this.$('load-button');
        break;
      default:
        throw new Error(`unknown type ${this.prop('type')}`);
    }
  }

  _handleCollectionUpdate({ src, doc }) {
    this.$('input').disabled = true;

    const updatedPagination = doc.getElementById(this.id);

    if (src === EVENT_SRC) {
      this.requestPending = false;
      this.loading = false;

      if (updatedPagination) {
        this.currentPage = Number(updatedPagination.getAttribute('current-page')) || this.currentPage;
        this.totalPages = Number(updatedPagination.getAttribute('total-pages')) || this.totalPages;
        this.setAttribute('current-page', this.currentPage);
        this.setAttribute('total-pages', this.totalPages);
      }

      this._checkEdge();

      if (this.restoringReturnPosition) {
        window.setTimeout(() => {
          if (this.currentPage < this.returnTargetPage) {
            this._loadNextPage();
          } else {
            this._finishReturnRestore();
          }
        }, 0);
      } else if (this.restoringHistoryPages) {
        window.setTimeout(() => {
          if (this.currentPage < this.historyTargetPage) {
            this._loadNextPage();
          } else {
            this._finishHistoryPageRestore();
          }
        }, 0);
      }
      return;
    }

    this.requestPending = false;
    this.restoringReturnPosition = false;
    this.returnState = null;
    this.restoringHistoryPages = false;
    this.historyRestoreCallback = null;

    if (!updatedPagination) return;

    this.currentPage = Number(updatedPagination.getAttribute('current-page')) || 1;
    this.totalPages = Number(updatedPagination.getAttribute('total-pages')) || 1;
    this.setAttribute('current-page', this.currentPage);
    this.setAttribute('total-pages', this.totalPages);
    this._checkEdge();
  }

  _handleButton() {
    this.$('load-button', { click: this._loadNextPage });
  }

  _initCurrentState() {
    this.currentPage = this.prop('current-page');
    this.totalPages = this.prop('total-pages');
    this.requestPending = false;
    this.restoringReturnPosition = false;
    this.restoringHistoryPages = false;
    this.historyRestoreCallback = null;
  }

  _handleEdgeScroll() {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.restoringReturnPosition && !this.restoringHistoryPages) {
        this._loadNextPage();
      }
    }, {
      rootMargin: `0px 0px ${OBSERVER_BOTTOM_OFFSET} 0px`
    });

    observer.observe(this);
    this.edgeObserver = observer;
  }

  _beginReturnRestore() {
    if (!isBackForwardNavigation()) {
      document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
      return;
    }

    const state = readReturnState();
    if (!state) {
      document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
      return;
    }

    const stateAge = Date.now() - Number(state.timestamp || 0);
    if ((state.collectionKey !== collectionKey() && state.urlKey !== currentUrlKey()) || stateAge >= RETURN_STATE_MAX_AGE) {
      clearReturnState();
      document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
      return;
    }

    hideCollectionForRestore();
    this.returnState = state;
    this.returnTargetPage = Math.min(
      this.totalPages,
      Math.max(this.currentPage, Number(state.page) || 1)
    );
    this.restoringReturnPosition = true;

    if (this.currentPage < this.returnTargetPage) {
      this._loadNextPage();
    } else {
      this._finishReturnRestore();
    }
  }

  _finishReturnRestore() {
    if (!this.returnState) return;

    this.restoringReturnPosition = false;
    const state = this.returnState;
    let attempts = 0;
    let stableFrames = 0;
    let previousTarget = null;

    const targetY = () => {
      const card = this._findReturnCard(state.cardPath || state.productPath);
      let target = Number(state.scrollY || 0);

      if (card && Number.isFinite(Number(state.cardViewportTop))) {
        target = window.pageYOffset + card.getBoundingClientRect().top - Number(state.cardViewportTop);
      }

      return Math.max(0, Number.isFinite(target) ? target : 0);
    };

    const settle = () => {
      attempts += 1;
      const target = targetY();
      // The restoring class overrides the theme's global smooth scrolling.
      window.scrollTo(0, target);

      requestAnimationFrame(() => {
        const nextTarget = targetY();
        const targetStable = previousTarget !== null && Math.abs(nextTarget - previousTarget) <= 1;
        const scrollStable = Math.abs(window.pageYOffset - nextTarget) <= 1;

        stableFrames = targetStable && scrollStable ? stableFrames + 1 : 0;
        previousTarget = nextTarget;

        if (stableFrames >= 2 || attempts >= 14) {
          window.scrollTo(0, nextTarget);
          requestAnimationFrame(() => {
            document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
            window.__yfCollectionReturnRestorePending = false;
            window.__yfCollectionProductNavigationPending = false;
            window.dispatchEvent(new CustomEvent('yf:collection-return-restored'));
            if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
            clearReturnState();
            this.returnState = null;
          });
          return;
        }

        settle();
      });
    };

    requestAnimationFrame(settle);
  }

  restoreHistoryPages(targetPage, done) {
    const target = Math.min(
      this.totalPages,
      Math.max(this.currentPage, Number(targetPage) || 1)
    );

    this.historyTargetPage = target;
    this.historyRestoreCallback = typeof done === 'function' ? done : null;
    this.restoringHistoryPages = true;

    if (this.currentPage < this.historyTargetPage) {
      this._loadNextPage();
    } else {
      this._finishHistoryPageRestore();
    }
  }

  _finishHistoryPageRestore() {
    this.restoringHistoryPages = false;
    const callback = this.historyRestoreCallback;
    this.historyRestoreCallback = null;
    if (typeof callback === 'function') callback();
  }

  _findReturnCard(productPath) {
    if (!productPath) return null;

    return Array.from(document.querySelectorAll('[data-yf-collection-product-card]'))
      .find((card) => {
        const rawUrl = card.getAttribute('data-yf-product-url');
        if (!rawUrl) return false;

        try {
          return new URL(rawUrl, window.location.origin).pathname === productPath;
        } catch (error) {
          return false;
        }
      }) || null;
  }

  _isSearchGrid() {
    return Boolean(this.closest('collection-provider[data-yf-shopify-search-grid]'));
  }

  _setSearchLoadError(state) {
    const label = this.querySelector('h5');

    if (state) {
      this.setAttribute('data-yf-load-error', 'true');
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      if (label) label.textContent = 'Tap to retry loading products';
    } else {
      this.removeAttribute('data-yf-load-error');
      this.removeAttribute('role');
      this.removeAttribute('tabindex');
      if (label) label.textContent = this._defaultLoadingLabel || 'Loading products';
    }
  }

  async _fetchSearchPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('page', String(page));

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Search page ${page} returned HTTP ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const updatedPagination = doc.getElementById(this.id);
    const productsId = `collection-products-${this.getAttribute('section-id')}`;
    const updatedProducts = doc.getElementById(productsId);
    const updatedContainer = updatedProducts?.querySelector('[data-element="products-container"]');

    if (!updatedPagination || !updatedContainer) {
      throw new Error(`Search page ${page} did not contain the expected product grid`);
    }

    const returnedPage = Number(updatedPagination.getAttribute('current-page')) || 0;
    if (returnedPage !== page) {
      throw new Error(`Search requested page ${page} but received page ${returnedPage || 'unknown'}`);
    }

    return doc;
  }

  _rememberLoadedPage(page) {
    const currentState = history.state && typeof history.state === 'object'
      ? history.state
      : {};

    history.replaceState({
      ...currentState,
      yfCollectionLoadedPage: Number(page) || 1
    }, '', window.location.href);
  }

  async _loadSearchNextPage() {
    const nextPage = this.currentPage + 1;

    this.requestPending = true;
    this.loading = true;
    this._setSearchLoadError(false);

    try {
      const doc = await this._fetchSearchPage(nextPage);
      const updatedPagination = doc.getElementById(this.id);
      const productsId = `collection-products-${this.getAttribute('section-id')}`;
      const updatedProducts = doc.getElementById(productsId);
      const updatedContainer = updatedProducts?.querySelector('[data-element="products-container"]');
      const currentProducts = document.getElementById(productsId);
      const currentContainer = currentProducts?.querySelector('[data-element="products-container"]');

      if (!updatedPagination || !updatedContainer || !currentContainer) {
        throw new Error(`Search page ${nextPage} could not be appended`);
      }

      /* Append the next Shopify result page directly in its original relevance
         order. The older event-bus path could leave requestPending/loading set
         after page two, which made every later load stall on "Loading products". */
      const cards = Array.from(updatedContainer.children);
      currentContainer.append(...cards);

      this.currentPage = Number(updatedPagination.getAttribute('current-page')) || nextPage;
      this.totalPages = Number(updatedPagination.getAttribute('total-pages')) || this.totalPages;
      this.setAttribute('current-page', this.currentPage);
      this.setAttribute('total-pages', this.totalPages);
      this._rememberLoadedPage(this.currentPage);

      this.requestPending = false;
      this.loading = false;
      this._checkEdge();

      window.dispatchEvent(new CustomEvent('yf:dynamic-content-ready', {
        detail: { source: 'search-infinite-scroll', page: this.currentPage }
      }));

      /* Re-arm the sentinel after every append. IntersectionObserver does not
         always emit a second entry when the sentinel remains near the viewport. */
      if (this.edgeObserver && !this.edge) {
        this.edgeObserver.unobserve(this);
        requestAnimationFrame(() => this.edgeObserver?.observe(this));
      }
    } catch (error) {
      console.error('[YF search pagination]', error);
      this.requestPending = false;
      this.loading = false;
      this._setSearchLoadError(true);
    }
  }

  async _loadNextPage() {
    if (this.requestPending) return;

    if (this.currentPage >= this.totalPages) {
      this.edge = true;
      this.loading = false;
      return;
    }

    // Search pagination is fetched directly from the complete search page.
    // This avoids the section-rendering race that could leave the spinner
    // running forever without appending the next batch of products.
    if (this._isSearchGrid()) {
      await this._loadSearchNextPage();
      return;
    }

    this.requestPending = true;
    this.loading = true;
    this.currentPage += 1;
    this.setAttribute('current-page', this.currentPage);

    this.$('input').disabled = false;
    this.$('input').value = this.currentPage;

    this.pub(Events.COLLECTION_CHANGE, {
      src: EVENT_SRC,
      formId: this.prop('form-id')
    });
  }

  _checkEdge() {
    this.edge = this.currentPage >= this.totalPages;
  }

  set loading(state) {
    $loading(this.$loadingTarget, state);
  }

  set edge(state) {
    $toggleDisplay(this.$loadingTarget, !state);
  }
});
